import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, callFunction } from '../firebase';
import { useCompanySession } from '../session';
import CompanyLayout from '../components/CompanyLayout';

function CompanyApiKeys() {
    const { session, loading } = useCompanySession();
    const [keys, setKeys] = useState([]);
    const [brokers, setBrokers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [plaintext, setPlaintext] = useState(null);
    const [form, setForm] = useState({ name: '', kind: 'company', brokerId: '' });
    const baseUrl = session ? (window.location.origin + '/api/v1/' + session.tenantId) : '';

    const load = async (tenantId) => {
        const keySnap = await getDocs(collection(db, 'tenants', tenantId, 'apiKeys'));
        const list = [];
        keySnap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        setKeys(list.sort((a, b) => String(a.prefix).localeCompare(String(b.prefix))));
        const userSnap = await getDocs(collection(db, 'tenants', tenantId, 'users'));
        const brokerList = [];
        userSnap.forEach((d) => {
            const row = d.data();
            if (row.role === 'broker') brokerList.push({ id: d.id, ...row });
        });
        setBrokers(brokerList);
    };

    useEffect(() => {
        if (session?.tenantId) load(session.tenantId).catch((err) => setMessage(err.message));
    }, [session]);

    const issue = async (e) => {
        e.preventDefault();
        setBusy(true);
        setMessage('');
        setPlaintext(null);
        try {
            const result = await callFunction('createCompanyApiKey', {
                tenantId: session.tenantId,
                name: form.name || (form.kind === 'broker' ? 'Broker API' : session.companyName + ' API'),
                kind: form.kind,
                brokerId: form.kind === 'broker' ? form.brokerId : null
            });
            setPlaintext(result);
            setMessage(result.warning || 'API key created');
            setForm({ name: '', kind: 'company', brokerId: '' });
            await load(session.tenantId);
        } catch (err) {
            setMessage(err.message || 'Could not create API key');
        }
        setBusy(false);
    };

    const revoke = async (keyId) => {
        if (!window.confirm('Revoke this API key? Connected brokers will lose access immediately.')) return;
        setBusy(true);
        try {
            await callFunction('revokeCompanyApiKey', { tenantId: session.tenantId, keyId });
            setMessage('API key revoked');
            await load(session.tenantId);
        } catch (err) {
            setMessage(err.message || 'Could not revoke key');
        }
        setBusy(false);
    };

    if (loading || !session) return <div className="p-5">Loading API settings...</div>;
    if (session.role !== 'admin') {
        return (
            <CompanyLayout session={session}>
                <div className="alert alert-warning">Only the company IT admin can issue broker API keys.</div>
            </CompanyLayout>
        );
    }

    const curlMe = plaintext
        ? 'curl -H "Authorization: Bearer ' + plaintext.apiKey + '" ' + baseUrl + '/me'
        : 'curl -H "Authorization: Bearer pk_live.' + session.tenantId + '.YOUR_KEY" ' + baseUrl + '/me';

    return (
        <CompanyLayout session={session}>
            <h3 className="mb-2">Broker &amp; company APIs</h3>
            <p className="text-muted">Each insurance company generates its own keys. Brokers connect to {session.companyName} with that key — they cannot see other companies.</p>
            {message && <div className="alert alert-info">{message}</div>}
            {plaintext && (
                <div className="alert alert-success">
                    <div className="fw-bold mb-1">Copy this key now. It is shown only once.</div>
                    <code className="d-block mb-2" style={{ wordBreak: 'break-all' }}>{plaintext.apiKey}</code>
                    <div className="small">Base URL: <code>{plaintext.baseUrl}</code></div>
                </div>
            )}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                    <h6>Issue an API key</h6>
                    <form className="row g-2" onSubmit={issue}>
                        <div className="col-md-4"><input className="form-control" placeholder="Key name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                        <div className="col-md-3">
                            <select className="form-select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                                <option value="company">Company (full access)</option>
                                <option value="broker">Broker (book only)</option>
                            </select>
                        </div>
                        {form.kind === 'broker' && (
                            <div className="col-md-3">
                                <select className="form-select" value={form.brokerId} onChange={(e) => setForm({ ...form, brokerId: e.target.value })} required>
                                    <option value="">Select connected broker</option>
                                    {brokers.map((b) => <option key={b.id} value={b.id}>{b.displayName || b.email}</option>)}
                                </select>
                            </div>
                        )}
                        <div className="col-md-2">
                            <button className="btn btn-dark w-100" disabled={busy || (form.kind === 'broker' && !form.brokerId)}>{busy ? 'Issuing...' : 'Generate'}</button>
                        </div>
                    </form>
                </div>
            </div>
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body table-responsive">
                    <table className="table mb-0">
                        <thead><tr><th>Name</th><th>Prefix</th><th>Kind</th><th>Broker</th><th>Status</th><th></th></tr></thead>
                        <tbody>
                            {keys.length === 0 && <tr><td colSpan="6" className="text-muted">No API keys yet. Generate one above, or use the sample keys issued on the server.</td></tr>}
                            {keys.map((k) => (
                                <tr key={k.id}>
                                    <td>{k.name}</td>
                                    <td><code>{k.prefix}…</code></td>
                                    <td>{k.kind}</td>
                                    <td>{k.brokerEmail || '—'}</td>
                                    <td>{k.status}</td>
                                    <td>{k.status === 'active' && <button className="btn btn-sm btn-outline-danger" onClick={() => revoke(k.id)}>Revoke</button>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <h6>Connect a broker over HTTPS</h6>
                    <p className="small text-muted mb-2">Gross premium uses <code>GP = NRP + WMF + GRA Taxes</code>. Policy states: DRAFT → QUOTE → ACTIVE → LAPSED.</p>
                    <pre className="bg-light p-3 small mb-3" style={{ whiteSpace: 'pre-wrap' }}>{curlMe}</pre>
                    <div className="small">
                        <div>Schema: <a href="/api/v1/schema" target="_blank" rel="noreferrer">{typeof window !== 'undefined' ? window.location.origin : ''}/api/v1/schema</a></div>
                        <div>List customers: <code>GET {baseUrl}/customers</code></div>
                        <div>Create quote: <code>POST {baseUrl}/quotes</code></div>
                        <div>Activate policy: <code>POST {baseUrl}/policies/&#123;id&#125;/activate</code></div>
                    </div>
                </div>
            </div>
        </CompanyLayout>
    );
}

export default CompanyApiKeys;
